import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Switch, 
  Button, 
  message, 
  Spin, 
  Typography, 
  Space,
  Row,
  Col,
  Badge,
  Tooltip,
  Divider,
  Avatar,
  Tag,
  Empty,
  Modal,
  Form,
  Input,
  Select
} from 'antd';
import { 
  UserOutlined, 
  SaveOutlined, 
  SafetyCertificateOutlined,
  SettingOutlined,
  DatabaseOutlined,
  EyeOutlined,
  BookOutlined,
  DashboardOutlined,
  RobotOutlined,
  TableOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { PERMISSIONS } from '../../component/permissions';
import { useAuth } from '../../component/AuthContext';

const { Title, Text, Paragraph } = Typography;

interface Role {
  id: number;
  name: string;
  permissions_mask: number;
}

interface PermissionFeature {
  key: keyof typeof PERMISSIONS;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

// Simple bitwise helpers
const hasReadPermission = (mask: number, perm: { READ: number }) =>
  Boolean(mask & perm.READ);
const hasWritePermission = (mask: number, perm: { WRITE: number }) =>
  Boolean(mask & perm.WRITE);
const setReadPermission = (
  mask: number,
  perm: { READ: number },
  enabled: boolean
) =>
  enabled
    ? mask | perm.READ
    : mask & ~perm.READ;
const setWritePermission = (
  mask: number,
  perm: { WRITE: number },
  enabled: boolean
) =>
  enabled
    ? mask | perm.WRITE
    : mask & ~perm.WRITE;

const RoleManagementPage: React.FC = () => {
  const { canWrite } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [isAddRoleModalVisible, setIsAddRoleModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Permission features with enhanced metadata
  const permissionFeatures: PermissionFeature[] = [
    {
      key: 'SYSTEM_MANAGEMENT',
      name: 'System Management',
      description: 'Configure system settings and manage infrastructure',
      icon: <SettingOutlined />,
      color: '#1890ff'
    },
    {
      key: 'DATASET_MANAGEMENT',
      name: 'Dataset Management',
      description: 'Create, edit, and manage training datasets',
      icon: <DatabaseOutlined />,
      color: '#52c41a'
    },
    {
      key: 'IMAGE_VIEW',
      name: 'Image Viewing',
      description: 'View and analyze processed images',
      icon: <EyeOutlined />,
      color: '#faad14'
    },
    {
      key: 'TEACHMODE',
      name: 'Teaching Mode',
      description: 'Access training and teaching functionality',
      icon: <BookOutlined />,
      color: '#722ed1'
    },
    {
      key: 'MODEL_MANAGEMENT',
      name: 'Model Management',
      description: 'Deploy, update, and manage AI models',
      icon: <RobotOutlined />,
      color: '#eb2f96'
    },
    {
      key: 'DASHBOARD',
      name: 'Dashboard Access',
      description: 'View analytics and system dashboards',
      icon: <DashboardOutlined />,
      color: '#13c2c2'
    },
    {
      key: 'MATRIX_MAPPING',
      name: 'Matrix Mapping',
      description: 'Map camera views to matrix labels for parts',
      icon: <TableOutlined />,
      color: '#fa8c16'
    }
  ];

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/roles`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error('Failed to load roles');
      }
      const data: Role[] = await res.json();
      setRoles(data);
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canWrite(PERMISSIONS.ROLE_MANAGEMENT)) {
      fetchRoles();
    } else {
      message.error("You don't have permission to manage roles.");
      setLoading(false);
    }
  }, [canWrite]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card style={{ textAlign: 'center', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text style={{ fontSize: 16, color: '#666' }}>Loading roles...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!canWrite(PERMISSIONS.ROLE_MANAGEMENT)) {
    return (
      <div style={{ 
        padding: 40, 
        textAlign: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card style={{ maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <SafetyCertificateOutlined style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }} />
          <Title level={3} style={{ color: '#ff4d4f' }}>Access Denied</Title>
          <Paragraph style={{ color: '#666', marginBottom: 0 }}>
            You don't have permission to manage user roles.
          </Paragraph>
        </Card>
      </div>
    );
  }

  const saveRole = async (role: Role) => {
    setSavingId(role.id);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/roles/${role.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ permissions_mask: role.permissions_mask }),
        }
      );
      if (!res.ok) throw new Error('Update failed');
      message.success(`Successfully updated ${role.name} permissions`);
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setSavingId(null);
    }
  };

  const updateRoleMask = (
    roleId: number,
    feature: keyof typeof PERMISSIONS,
    type: 'read' | 'write',
    enabled: boolean
  ) => {
    setRoles((rs) =>
      rs.map((r) => {
        if (r.id !== roleId) return r;
        const permObj = PERMISSIONS[feature];
        const newMask =
          type === 'read'
            ? setReadPermission(r.permissions_mask, permObj, enabled)
            : setWritePermission(r.permissions_mask, permObj, enabled);
        return { ...r, permissions_mask: newMask };
      })
    );
  };

  const addRole = async () => {
    try {
      const values = await form.validateFields();
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/roles`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: values.name, permissions_mask: 0 }),
        }
      );
      if (!res.ok) throw new Error('Failed to create role');
      
      message.success(`Successfully created role: ${values.name}`);
      await fetchRoles();
      setIsAddRoleModalVisible(false);
      form.resetFields();
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const getPermissionCount = (role: Role) => {
    let count = 0;
    permissionFeatures.forEach(feature => {
      const perm = PERMISSIONS[feature.key];
      if (hasReadPermission(role.permissions_mask, perm) || hasWritePermission(role.permissions_mask, perm)) {
        count++;
      }
    });
    return count;
  };

  const renderPermissionCard = (role: Role, feature: PermissionFeature) => {
    const perm = PERMISSIONS[feature.key];
    const hasRead = hasReadPermission(role.permissions_mask, perm);
    const hasWrite = hasWritePermission(role.permissions_mask, perm);
    const hasAnyPermission = hasRead || hasWrite;

    return (
      <Card
        key={feature.key}
        size="small"
        style={{ 
          marginBottom: 8,
          border: hasAnyPermission ? `1px solid ${feature.color}40` : '1px solid #f0f0f0',
          borderRadius: 6,
          boxShadow: hasAnyPermission ? `0 2px 6px ${feature.color}15` : '0 1px 4px rgba(0,0,0,0.04)',
          transition: 'all 0.3s ease',
          background: hasAnyPermission ? `${feature.color}05` : '#fff'
        }}
        bodyStyle={{ padding: 12 }}
      >
        <Row align="middle" justify="space-between">
          <Col flex={1}>
            <Space align="center" size="small">
              <div style={{ 
                background: feature.color, 
                color: 'white', 
                padding: 4, 
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24
              }}>
                {React.cloneElement(feature.icon as React.ReactElement, { style: { fontSize: 12 } })}
              </div>
              <div>
                <Text strong style={{ fontSize: 12, color: '#262626', display: 'block' }}>
                  {feature.name}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                  {feature.description.length > 35 ? feature.description.substring(0, 35) + '...' : feature.description}
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space size="small">
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 2 }}>
                  <Text style={{ fontSize: 10, fontWeight: 500, color: '#666' }}>R</Text>
                </div>
                <Switch
                  size="small"
                  checked={hasRead}
                  onChange={(checked) => updateRoleMask(role.id, feature.key, 'read', checked)}
                  style={{
                    backgroundColor: hasRead ? feature.color : undefined,
                    minWidth: 28
                  }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 2 }}>
                  <Text style={{ fontSize: 10, fontWeight: 500, color: '#666' }}>W</Text>
                </div>
                <Switch
                  size="small"
                  checked={hasWrite}
                  onChange={(checked) => updateRoleMask(role.id, feature.key, 'write', checked)}
                  style={{
                    backgroundColor: hasWrite ? feature.color : undefined,
                    minWidth: 28
                  }}
                />
              </div>
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderRoleCard = (role: Role) => {
    const permissionCount = getPermissionCount(role);
    const isSystemRole = role.name.toLowerCase().includes('admin') || role.name.toLowerCase().includes('system');

    return (
      <Card
        key={role.id}
        style={{
          border: 'none',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          borderRadius: 12,
          overflow: 'hidden',
          height: '100%'
        }}
      >
        {/* Role Header */}
        <div style={{
          background: isSystemRole 
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
            : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          padding: '16px 20px',
          marginBottom: 16,
          marginTop: -24,
          marginLeft: -24,
          marginRight: -24
        }}>
          <Row align="middle" justify="space-between">
            <Col flex={1}>
              <Space align="center">
                <Avatar 
                  size={36} 
                  icon={<UserOutlined />} 
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    border: '2px solid rgba(255,255,255,0.3)' 
                  }} 
                />
                <div>
                  <Title level={4} style={{ margin: 0, color: 'white', fontSize: 16 }}>
                    {role.name}
                  </Title>
                  <Space size="small">
                    <Badge 
                      count={permissionCount} 
                      style={{ backgroundColor: 'rgba(255,255,255,0.9)', color: '#1890ff' }}
                    />
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
                      permissions
                    </Text>
                  </Space>
                </div>
              </Space>
            </Col>
          </Row>
          <div style={{ marginTop: 12 }}>
            <Button
              type="primary"
              ghost
              icon={<SaveOutlined />}
              onClick={() => saveRole(role)}
              loading={savingId === role.id}
              size="small"
              block
              style={{
                borderColor: 'rgba(255,255,255,0.8)',
                color: 'white',
                height: 32
              }}
            >
              Save Changes
            </Button>
          </div>
        </div>

        {/* Permissions Grid */}
        <div>
          <Title level={5} style={{ marginBottom: 12, color: '#666', fontSize: 14 }}>
            Feature Permissions
          </Title>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {permissionFeatures.map(feature => renderPermissionCard(role, feature))}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div style={{ 
      padding: '24px 24px 0', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <Card style={{ marginBottom: 24, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Space align="center">
              <SafetyCertificateOutlined style={{ fontSize: 28, color: '#1890ff' }} />
              <div>
                <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                  Role Management
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Configure user roles and permission levels
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                onClick={() => setIsAddRoleModalVisible(true)}
                style={{ 
                  borderRadius: 8,
                  height: 40,
                  paddingLeft: 20,
                  paddingRight: 20
                }}
              >
                Add New Role
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Roles Content */}
      <div>
        {roles.length === 0 ? (
          <Card style={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Empty 
              description="No roles configured"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: '60px 0' }}
            >
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setIsAddRoleModalVisible(true)}
              >
                Create First Role
              </Button>
            </Empty>
          </Card>
        ) : (
          <Row gutter={[24, 24]}>
            {roles.map(role => (
              <Col 
                key={role.id} 
                xs={24} 
                sm={24} 
                md={12} 
                lg={8} 
                xl={6} 
                xxl={6}
              >
                {renderRoleCard(role)}
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* Add Role Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            Create New Role
          </Space>
        }
        open={isAddRoleModalVisible}
        onOk={addRole}
        onCancel={() => {
          setIsAddRoleModalVisible(false);
          form.resetFields();
        }}
        okText="Create Role"
        okButtonProps={{ icon: <SaveOutlined /> }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Role Name"
            rules={[
              { required: true, message: 'Please enter a role name' },
              { min: 2, message: 'Role name must be at least 2 characters' },
              { pattern: /^[a-zA-Z0-9\s_-]+$/, message: 'Role name can only contain letters, numbers, spaces, underscores, and hyphens' }
            ]}
          >
            <Input placeholder="e.g. Data Analyst, System Administrator" />
          </Form.Item>
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              The role will be created with no permissions. You can configure permissions after creation.
            </Text>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagementPage;